import React, { useState } from 'react';
import { Button, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import { loginSuccess } from '../../../api/auth/authSlice';
import { authApiClient } from '../../../api/client/axios';
import { showSnackbar } from '../../../store/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { map } from 'lodash';
import appConfig from '../../../config/appConfig';

export const LoginEmailSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

const LoginForm = ({ redirectTo }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    setError,
  } = useForm({
    resolver: yupResolver(LoginEmailSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/login', data)
      .then((res) => {
        if (res.data.status?.toLowerCase() === 'otp_required') {
          const message = res.data.message || 'OTP sent to your email';
          dispatch(
            showSnackbar({
              message: message,
              severity: 'success',
            }),
          );
          navigate('/login', {
            replace: true,
            state: { email: data.email, stage: 'otp_required', message: message, redirectTo: redirectTo },
          });
        } else {
          dispatch(loginSuccess());
          navigate(`/`, { replace: true });
        }
      })
      .catch((err) => {
        console.log(err);
        if (err?.response?.data?.errors) {
          map(err?.response?.data?.errors, (value, key) => {
            setError(key, { message: value, type: 'string' });
          });
        } else if (err?.response?.data?.errors?.message) {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.errors?.message,
              severity: 'warning',
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || 'Something went wrong!',
              severity: 'warning',
            }),
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt={`${appConfig.brandName} logo`} width="50%" />
      </Stack>
      <Stack spacing={2}>
        <CustomTextInput data-testid="email-input" name="email" label="Email" control={control} errors={errors} />
        <CustomTextInput
          data-testid="password-input"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Typography variant="body2">
          <Link to="/forgot-password" style={{ textDecoration: 'none', paddingBottom: '5rem' }}>
            Forgot Password?
          </Link>
        </Typography>
        <Button
          data-testid="sign-in-button"
          type="button"
          variant="contained"
          className="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {loading ? 'Logging in...' : 'Sign In'}
        </Button>
      </Stack>
    </>
  );
};

export default LoginForm;
